import React, { ReactNode, useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Typography, ArrowDropDownIconSvg } from '@dev/ee-components';
import { useDispatch, useSelector } from 'react-redux';
import { map } from 'lodash-es';
import styled from 'styled-components';
import { ClickAwayListener, PaperProps } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import { getDomainsStore } from '@/store/domain/actions';
import { ShowDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import { getValidEmailsStore } from '@/store/validemails/actions';
import { useGetVerifiedDomains } from '@/components/CampaignCreator/hooks/useGetVerifiedDomains';
import { useGetVerifiedEmails } from '@/components/CampaignCreator/hooks/useGetVerifiedEmails';
import { useCampaignScreensPathsChecker } from '@/components/CampaignCreator/hooks/useCampaignScreensPathsChecker';
import { getCampainCreatorStore, SaveCampaignStart } from '@/store/campaignCreator/actions';
import { CustomPaper } from './components/CustomPaper';
import { FromEmailTip } from './components/FromEmailTip';
import { EmailDropdownType, FromEmailEnum, EmailOptionsType } from '../../SubjectAndContentInterface';
import { TipWrapper } from '../styledComponents';

const GroupHeader = styled.div`
    padding-top: 12px;
    padding-left: 16px;
`;

const IconWrapper = styled.span`
    width: 20px;
    height: 20px;
`;

const Wrapper = styled.div`
    display: flex;
`;

const AutocompleteWrapper = styled.div`
    flex-grow: 1;
    .MuiInputBase-root {
        padding-right: 32px;
    }
`;

type GroupParams = {
    key: string;
    group: string;
    children: React.ReactNode | React.ReactNode[];
};

export const EmailDropdown: FC<EmailDropdownType> = (props: EmailDropdownType): JSX.Element => {
    const { label, setFromEmail, fromEmail, hasError, id, isTipVisible, errorMessage } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { domains, loadingDomains } = useSelector(getDomainsStore);
    const { emails, loadingEmails } = useSelector(getValidEmailsStore);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [isSearchVisible, setSearchVisible] = useState(false);
    const { isDetailsScreen } = useCampaignScreensPathsChecker();
    const { activeTemplateIndex } = useSelector(getCampainCreatorStore);
    const verifiedDomains = useGetVerifiedDomains();
    const verifiedEmails = useGetVerifiedEmails();

    const isLoading = loadingDomains || loadingEmails;
    const isSendersAlreadyLoaded = !!emails && !!domains;

    const getDomainsGroup = () => {
        const domainsNames = map(verifiedDomains, (domain) => domain.Domain.split(' (')[0]);
        return map(domainsNames, (domain) => ({
            name: domain,
            value: `@${domain}`,
            group: FromEmailEnum.Domains,
        }));
    };

    const getValidEmailsGroup = () =>
        map(verifiedEmails, (email) => ({
            name: email,
            value: email,
            group: FromEmailEnum.Emails,
        }));

    const domainsGroup = getDomainsGroup();
    const validEmailsGroup = getValidEmailsGroup();

    const getFilterOptions = (options: EmailOptionsType[]) => {
        const results: Array<EmailOptionsType> = [...options];
        let filteredResults = results;

        if (!!search && search !== '') {
            filteredResults = results.filter((e) => e.name.includes(search));
        }
        if (!!options?.length && options?.length > 20) {
            setSearchVisible(true);
        }
        return filteredResults;
    };

    const renderGroup = (params: GroupParams): ReactNode[] => [
        <GroupHeader key={params.key}>
            <Typography variant="body2">{params.group}</Typography>
        </GroupHeader>,
        params.children,
    ];

    const handleOnChange = (): void => {
        const inputSelector = `#campaign-creator-emaildropdown-emailfrom-${activeTemplateIndex}`;
        const input = document.querySelector<HTMLInputElement>(inputSelector);
        setTimeout(() => {
            input.setSelectionRange(0, 0);
        }, 0);
    };

    const handleOnInputChange = (email: string): void => {
        setFromEmail(email);
        setOpen(false);
    };
    /*
    const handleOnBlur = (email: string): void => {
        setFromEmail(email);
        isFromEmail && dispatch(CheckEnteredDomainStart(fromEmail));
    };
*/
    const handleOnClickAway = (): void => {
        setOpen(false);
    };

    const saveAndRedirect = (): void => {
        dispatch(SaveCampaignStart({ isDraft: true, openDomainValidationScreen: true }));
    };

    const openExitModal = (): void => {
        const dialogParams: ShowDialogParams = {
            title: t('exitcampaigncreator'),
            confirmLabel: t('saveandexit'),
            body: t('redirecttodomainverification'),
            onClickConfirm: saveAndRedirect,
            cancelLabel: t('cancel'),
        };
        dispatch(ShowDialog(dialogParams));
    };

    const renderCustomPaper = (paperProps: PaperProps): JSX.Element => (
        <CustomPaper
            {...paperProps}
            isSearchVisible={isSearchVisible}
            handleSearchInput={(e) => setSearch(e.target.value)}
            searchValue={search}
            handleClearSearch={() => setSearch('')}
            handleClose={() => setOpen(false)}
            openExitModal={openExitModal}
        />
    );

    return isLoading && !isSendersAlreadyLoaded ? (
        <Skeleton style={{ width: '100%', height: 44, marginTop: 20 }} />
    ) : (
        <ClickAwayListener onClickAway={handleOnClickAway}>
            <div key="click-away-wrapper">
                <Wrapper>
                    <AutocompleteWrapper>
                        <Autocomplete
                            id={id}
                            defaultValue={{
                                name: fromEmail,
                                value: fromEmail,
                            }}
                            inputValue={fromEmail}
                            options={[...domainsGroup, ...validEmailsGroup]}
                            freeSolo
                            disableClearable
                            placeholder={t('enterfromemail')}
                            popupIcon={
                                !isDetailsScreen && (
                                    <IconWrapper onClick={() => setOpen(!open)}>
                                        <ArrowDropDownIconSvg />
                                    </IconWrapper>
                                )
                            }
                            forcePopupIcon={!isDetailsScreen}
                            onChange={handleOnChange}
                            onInputChange={(e, value) => handleOnInputChange(value)}
                            //onBlur={(e) => handleOnBlur(e.target.value)}
                            label={label}
                            groupBy={(option) => option.group}
                            getOptionLabel={(option) => option.value}
                            filterOptions={getFilterOptions}
                            renderOption={(option) => <Typography variant="body1">{option.name}</Typography>}
                            renderGroup={renderGroup}
                            PaperComponent={renderCustomPaper}
                            open={open}
                            error={hasError}
                            errorMessage={errorMessage}
                            readOnly={isDetailsScreen}
                        />
                    </AutocompleteWrapper>
                    {!isDetailsScreen && (
                        <TipWrapper>
                            <FromEmailTip isTipVisible={isTipVisible} openExitModal={openExitModal} />
                        </TipWrapper>
                    )}
                </Wrapper>
            </div>
        </ClickAwayListener>
    );
};
